import { Controller } from "@hotwired/stimulus"

// Phase 3b — admin: re-home listings between categories by drag.
//
// Vanilla HTML5 drag-and-drop (no Sortable.js). Each category bucket is
// a dropzone; dragging a listing card into a bucket PATCHes the
// provider's integration_category_id and moves the card in the DOM. The
// "Uncategorized" bucket persists a null category. No reorder-within-
// bucket — providers have no position column, so this is a move only.
export default class extends Controller {
  static targets = ["bucket", "card", "count"]
  static values = { changeUrlTemplate: String }

  onDragStart(event) {
    const card = event.currentTarget
    this._draggingId = card.dataset.id
    this._sourceBucket = card.closest("[data-integration-admin-by-category-target='bucket']")
    card.classList.add("opacity-50")
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = "move"
      event.dataTransfer.setData("text/plain", this._draggingId)
    }
  }

  onDragEnd(event) {
    event.currentTarget.classList.remove("opacity-50")
    this.bucketTargets.forEach(b => b.classList.remove("ring-2", "ring-brand", "bg-brand-50/40"))
  }

  onDragOver(event) {
    event.preventDefault()
    if (event.dataTransfer) event.dataTransfer.dropEffect = "move"
    const bucket = event.currentTarget
    if (bucket !== this._sourceBucket) bucket.classList.add("ring-2", "ring-brand", "bg-brand-50/40")
  }

  onDragLeave(event) {
    // Only clear when the pointer actually left the bucket (not a child).
    const bucket = event.currentTarget
    if (!bucket.contains(event.relatedTarget)) {
      bucket.classList.remove("ring-2", "ring-brand", "bg-brand-50/40")
    }
  }

  onDrop(event) {
    event.preventDefault()
    const bucket = event.currentTarget
    bucket.classList.remove("ring-2", "ring-brand", "bg-brand-50/40")
    if (!this._draggingId) return

    const card = this.cardTargets.find(c => c.dataset.id === this._draggingId)
    if (!card) return
    const targetCategoryId = bucket.dataset.categoryId || ""
    const sourceBucket = this._sourceBucket
    if (sourceBucket === bucket) return

    bucket.querySelector("[data-empty-hint]")?.remove()
    bucket.appendChild(card)
    this._refreshCounts()
    this._persist(this._draggingId, targetCategoryId, card, sourceBucket, bucket)
    this._draggingId = null
    this._sourceBucket = null
  }

  async _persist(id, categoryId, card, sourceBucket, targetBucket) {
    const url = this.changeUrlTemplateValue.replace("ID", id)
    const body = new FormData()
    body.append("integration_category_id", categoryId)
    try {
      const res = await fetch(url, {
        method: "PATCH",
        body,
        credentials: "same-origin",
        headers: { "X-CSRF-Token": this._csrfToken(), "Accept": "application/json" }
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok && data.ok) {
        this._toast(data.message || "Listing moved.", "notice")
      } else {
        // Roll back the DOM move on failure.
        sourceBucket.appendChild(card)
        this._refreshCounts()
        this._toast(data.message || "Couldn't move listing.", "alert")
      }
    } catch (_) {
      sourceBucket.appendChild(card)
      this._refreshCounts()
      this._toast("Network error — try again.", "alert")
    }
  }

  _refreshCounts() {
    this.countTargets.forEach(badge => {
      const bucket = this.bucketTargets.find(b => b.dataset.categoryId === badge.dataset.categoryId)
      if (bucket) badge.textContent = bucket.querySelectorAll("[data-integration-admin-by-category-target='card']").length
    })
  }

  _csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  _toast(message, type) {
    const div = document.createElement("div")
    div.className = type === "notice" ? "flash-toast-notice" : "flash-toast-alert"
    div.dataset.controller = "flash"
    div.dataset.flashDismissAfterValue = "5000"
    div.textContent = message
    document.body.prepend(div)
  }
}
